import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../services/config';

export interface ThreeDSegmentation {
  uuid: string;
  frameIcid: string;
  frameIndex: number;
  dicomIcid?: string;
  segmentationClassValue: string | null;
  paths: string[] | null;
  pno: number[] | null;
  userEmail?: string | null;
}

export interface ThreeDSegmentationPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: ThreeDSegmentation[];
}

export interface ThreeDSegmentationPageParams {
  studyIcid: string;
  page: number;
  size: number;
}

export interface ThreeDDicom {
  icid: string;
  physicalDeltaX: number | null;
}

export interface ThreeDDicomPage {
  next: string | null;
  results: ThreeDDicom[];
}

export interface ThreeDFrame {
  file: string | null;
  icid: string | null;
  index: number;
}

type FramesResponse = ThreeDFrame[] | { results: ThreeDFrame[] };

export interface ThreeDStudy {
  icid: string;
  foreignId: string | null;
}

export interface ThreeDAnnotationSetOwner {
  name: string | null;
  userEmail: string;
  userName: string;
  uuid: string;
}

type RawThreeDStudy = {
  icid: string;
  foreignId?: string | null;
  foreign_id?: string | null;
};

type StudiesResponse = RawThreeDStudy[] | { results: RawThreeDStudy[] };
type AnnotationSetOwnersResponse =
  | ThreeDAnnotationSetOwner[]
  | { results: ThreeDAnnotationSetOwner[] };

const extractStudies = (response: StudiesResponse) =>
  (Array.isArray(response) ? response : response.results).map(study => ({
    icid: study.icid,
    foreignId: study.foreignId ?? study.foreign_id ?? null,
  }));

const labelerBaseQuery = fetchBaseQuery({
  baseUrl: config.LABELER_API_URL,
  credentials: 'include',
  prepareHeaders: headers => {
    const token = localStorage.getItem('token');
    if (token) headers.set('Authorization', `Token ${token}`);
    return headers;
  },
});

export const threeDStudyAPI = createApi({
  reducerPath: 'threeDStudyAPI',
  baseQuery: labelerBaseQuery,
  endpoints: builder => ({
    getSegmentationLabelsFor3D: builder.query<ThreeDSegmentationPage, ThreeDSegmentationPageParams>(
      {
        query: ({ studyIcid, page, size }) =>
          `api/v1/on-review-segmentation-labels/?study_icid=${encodeURIComponent(studyIcid)}&page=${page}&size=${size}`,
      }
    ),
    getSegmentationPredictionsFor3D: builder.query<
      ThreeDSegmentationPage,
      ThreeDSegmentationPageParams
    >({
      query: ({ studyIcid, page, size }) =>
        `api/v1/segmentation-predictions/?study_icid=${encodeURIComponent(studyIcid)}&page=${page}&size=${size}`,
    }),
    getSegmentationReferencesFor3D: builder.query<
      ThreeDSegmentationPage,
      ThreeDSegmentationPageParams
    >({
      query: ({ studyIcid, page, size }) =>
        `api/v1/segmentation-references/?study_icid=${encodeURIComponent(studyIcid)}&page=${page}&size=${size}`,
    }),
    getDicomsFor3D: builder.query<ThreeDDicomPage, ThreeDSegmentationPageParams>({
      query: ({ studyIcid, page, size }) =>
        `api/v1/dicoms/?study_icid=${encodeURIComponent(studyIcid)}&page=${page}&size=${size}`,
    }),
    getFramesFor3D: builder.query<ThreeDFrame[], string>({
      query: dicomIcid => ({
        url: 'api/v1/frames/',
        params: {
          dicom_icid: dicomIcid,
          paginate: false,
        },
      }),
      transformResponse: (response: FramesResponse) =>
        Array.isArray(response) ? response : response.results,
    }),
    getAbbottStudiesFor3D: builder.query<ThreeDStudy[], void>({
      query: () => ({
        url: 'api/v1/studies/',
        params: {
          datasource: 'ABBOTT',
          has_pno: true,
          paginate: false,
          summary: true,
        },
      }),
      transformResponse: extractStudies,
    }),
    getAnnotationSetOwnersFor3D: builder.query<ThreeDAnnotationSetOwner[], string>({
      query: studyIcid => ({
        url: 'api/v1/full-study-annotation-sets/',
        params: {
          paginate: false,
          study_icid: studyIcid,
        },
      }),
      transformResponse: (response: AnnotationSetOwnersResponse) =>
        Array.isArray(response) ? response : response.results,
    }),
  }),
});

export const {
  useLazyGetSegmentationLabelsFor3DQuery,
  useLazyGetSegmentationPredictionsFor3DQuery,
  useLazyGetSegmentationReferencesFor3DQuery,
  useLazyGetDicomsFor3DQuery,
  useLazyGetFramesFor3DQuery,
  useGetAbbottStudiesFor3DQuery,
  useGetAnnotationSetOwnersFor3DQuery,
} = threeDStudyAPI;
